import { BuySellTabContentProps } from '@shared/types/frontend/assetPage';
import { X } from 'lucide-react';
import { DefaultStages } from '@shared/types/frontend';
import { useEffect, useRef, useState } from 'react';
import { GoSearch } from 'react-icons/go';
import { NormieRowType } from '@shared/types/frontend/tables';
import ImageWithFallback from '@/components/shared/ImageWithFallback';
import { useSearchHome } from '@/contexts/SearchHomeProvider';
import NormieTable from '@/components/explore/desktop/normie-mode/NormieTable';
import LaunchPadView from '@/components/explore/LaunchPadView';
import DFiltersController from '@/components/explore/desktop/filters/DFiltersController';
import FilterByTokenType from '@/components/explore/desktop/filters/FilterByTokenType';
import Loading from '@/components/shared/Loading';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DTCashResults from '@/components/explore/desktop/normie-mode/DTCashResults';
import TopTokensResults from '@/components/explore/desktop/normie-mode/TopTokensResults';
import { Switch } from '@/components/ui/switch';
import classNames from '@/lib/misc/classNames';
const SelectToTokenListModalContent = ({
  form,
  handleToggleModal
}: {
  form: BuySellTabContentProps['form'];
  handleToggleModal: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    setKeyword,
    network,
    setNetwork,
    timeFrame,
    setTimeFrame,
    defaultNetwork,
    resultsToUse,
    useUnprotectedSearch,
    setUseUnprotectedSearch,
    mode,
    setMode,
    stage,
    handleSearch,
    keyword,
    wildWest,
    showWildWest
  } = useSearchHome();
  const previousMode = useRef(mode);
  const _handleOnToTokenSelect = (toToken: any) => {
    const _toToken = {
      contract_decimals: toToken?.decimals,
      contract_name: toToken?.name || toToken?.info?.name,
      contract_ticker_symbol: toToken?.symbol || toToken?.symbol,
      contract_address: toToken?.address,
      contract_display_name: toToken?.info?.name || toToken?.name,
      logo_url: toToken?.imageLargeUrl ?? toToken?.imageThumbUrl ?? toToken?.info?.imageThumbUrl,
      network: toToken?.networkId
    };
    form.setValue('toToken', _toToken);
    setKeyword('');
    setSearchTerm('');
    handleToggleModal();
    return;
  };

  // Debouncing logic: delay the actual handleSearch execution
  useEffect(() => {
    // If the searchTerm is empty, we go back to the previous selected mode
    if (searchTerm) {
      if (mode !== 'search') {
        previousMode.current = mode;
      }
      setMode('search');
    } else {
      if (mode === 'search') {
        setMode(previousMode.current);
      }
    }
    const handler = setTimeout(() => {
      if (!keyword && mode !== 'search' && mode != 'recent') {
        setSearchTerm('');
        handleSearch('');
        return;
      } else {
        handleSearch(searchTerm);
      }
    }, 300);

    // Cleanup the timeout if the searchTerm changes before delay
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, handleSearch]);
  const tokensToUse = resultsToUse();
  return <div className='h-full flex flex-col gap-4' data-sentry-component="SelectToTokenListModalContent" data-sentry-source-file="SelectToTokenListModalContent.tsx">
      <div className='flex items-center justify-between px-2'>
        <div className={`w-full h-[34px] flex gap-2 items-center text-genius-cream bg-genius-blue rounded-full px-[12px] py-[10px]`}>
          <GoSearch className='w-[18px] h-[18px]' data-sentry-element="GoSearch" data-sentry-source-file="SelectToTokenListModalContent.tsx" />
          <Input type='text' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder='Search' className={`bg-transparent border-none  rounded-md`} data-sentry-element="Input" data-sentry-source-file="SelectToTokenListModalContent.tsx" />
        </div>
        <Button type='button' variant='ghost' onClick={() => handleToggleModal()} data-sentry-element="Button" data-sentry-source-file="SelectToTokenListModalContent.tsx">
          <X className='w-4 h-4' data-sentry-element="X" data-sentry-source-file="SelectToTokenListModalContent.tsx" />
        </Button>
      </div>
      {searchTerm && <div className={classNames('flex items-center justify-between gap-2 pb-4 w-full text-md px-4 relative')}>
          <div className=''>Safe Search</div>
          <Switch checked={!wildWest} onClick={() => {
        showWildWest(!wildWest);
      }} />
          <div className='border-b top-11 border-genius-blue w-[1000px] -translate-x-32 absolute'></div>
        </div>}

      <div className='flex-1 flex flex-col overflow-hidden'>
        {mode !== 'search' ? <div className='flex justify-between px-2'>
            <FilterByTokenType setKeyword={setKeyword} mode={mode} setMode={setMode} customHandler />
            <div className='items-center flex'>
              <DFiltersController network={network} defaultNetwork={defaultNetwork} setNetwork={setNetwork} timeFrame={timeFrame} setTimeFrame={setTimeFrame} customHandler />
            </div>
          </div> : null}

        <div className='flex-1 flex flex-col mt-2'>
          {mode === 'cash' ? <DTCashResults onTokenClick={_handleOnToTokenSelect} /> : mode == 'launchpad' ? <LaunchPadView /> : mode === 'top' || mode === 'native' ? <TopTokensResults mode={mode} resultsToUse={resultsToUse()} timeFrame={timeFrame} onTokenClick={_handleOnToTokenSelect} /> : <>
              {stage === DefaultStages.loading && <div className='h-[65vh] w-full flex items-center flex-col justify-center'>
                  <Loading className='h-8 w-8' />
                </div>}
              {stage === DefaultStages.results && <>
                  {(mode === 'trending' || mode === 'gainers' || mode === 'new') && <div className='mx-4 p-2 text-xs text-genius-cream bg-red-500 bg-opacity-10 border-red-400 border-[1px] rounded-lg my-4'>
                      Tokens here are algorithmically sourced from on-chain data. They are not
                      vetted for honeypots or scams. Genius is not responsible for lost funds.
                      Exercise caution when trading.
                    </div>}
                  <NormieTable key={`${mode}-${timeFrame}`} resultsToUse={[...(mode === 'search' ? [{
              type: NormieRowType.HEADER,
              content: 'Search Results',
              className: 'text-[20px] text-genius-cream ml-4',
              height: 34
            }] : []), ...(mode === 'recent' ? [{
              type: NormieRowType.HEADER,
              content: 'Recent Items',
              className: 'text-[20px] text-genius-cream ml-4',
              height: 34
            }] : []), ...(tokensToUse ?? [])]} setKeyword={setKeyword} timeFrame={timeFrame} customHandler={_handleOnToTokenSelect} />
                </>}
              {stage === DefaultStages.noResults && <div className='text-genius-cream/50 mx-auto justify-center ta-c text-base flex items-center mt-[150px] flex-col'>
                  <ImageWithFallback src='/static/icons/nothingcircle.png' width={80} height={80} alt='nothing here' className='my-8' />
                  {mode === 'search' ? 'No Safe Search Results' : mode === 'new' ? 'No New Tokens in the last 24 hours' : 'No Results'}
                  {mode === 'search' && !useUnprotectedSearch && <div className='bg-genius-blue rounded-full p-2 px-4 text-genius-cream mt-4 text-[12px]' onClick={() => {
              setUseUnprotectedSearch(true);
            }}>
                      Show Low Volume Results
                    </div>}
                </div>}
            </>}
        </div>
      </div>
    </div>;
};
export default SelectToTokenListModalContent;